import React, { Component } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, ReferenceLine, Cell } from 'recharts';
import { Download } from 'lucide-react';
// --- Mock Data ---
const premiumTrendData = [{
  month: 'Jan',
  gwp: 1.2,
  earned: 1.1
}, {
  month: 'Feb',
  gwp: 1.4,
  earned: 1.2
}, {
  month: 'Mar',
  gwp: 1.5,
  earned: 1.3
}, {
  month: 'Apr',
  gwp: 1.3,
  earned: 1.3
}, {
  month: 'May',
  gwp: 1.6,
  earned: 1.4
}, {
  month: 'Jun',
  gwp: 1.8,
  earned: 1.5
}, {
  month: 'Jul',
  gwp: 1.7,
  earned: 1.6
}, {
  month: 'Aug',
  gwp: 1.9,
  earned: 1.7
}, {
  month: 'Sep',
  gwp: 2.1,
  earned: 1.8
}, {
  month: 'Oct',
  gwp: 2.0,
  earned: 1.9
}, {
  month: 'Nov',
  gwp: 2.2,
  earned: 2.0
}, {
  month: 'Dec',
  gwp: 2.4,
  earned: 2.1
}];
const waterfallData = [{
  name: 'Gross Premium',
  value: 15.2,
  fill: '#000000'
}, {
  name: 'Ceded Premium',
  value: -3.8,
  fill: '#EF4444'
}, {
  name: 'Net Premium',
  value: 11.4,
  fill: '#22C55E'
}];
const underwritingData = [{
  quarter: 'Q1 2024',
  premium: 3.5,
  claims: 2.1,
  expenses: 0.8
}, {
  quarter: 'Q2 2024',
  premium: 3.8,
  claims: 2.3,
  expenses: 0.9
}, {
  quarter: 'Q3 2024',
  premium: 4.1,
  claims: 2.5,
  expenses: 1.0
}, {
  quarter: 'Q4 2024',
  premium: 4.5,
  claims: 2.8,
  expenses: 1.1
}];
const investmentData = [{
  month: 'Jan',
  income: 45,
  returns: 4.2
}, {
  month: 'Feb',
  income: 48,
  returns: 4.5
}, {
  month: 'Mar',
  income: 52,
  returns: 4.8
}, {
  month: 'Apr',
  income: 50,
  returns: 4.6
}, {
  month: 'May',
  income: 55,
  returns: 5.1
}, {
  month: 'Jun',
  income: 58,
  returns: 5.3
}, {
  month: 'Jul',
  income: 60,
  returns: 5.5
}, {
  month: 'Aug',
  income: 62,
  returns: 5.7
}, {
  month: 'Sep',
  income: 65,
  returns: 6.0
}, {
  month: 'Oct',
  income: 63,
  returns: 5.8
}, {
  month: 'Nov',
  income: 68,
  returns: 6.2
}, {
  month: 'Dec',
  income: 72,
  returns: 6.5
}];
// --- Brand Colors ---
const COLORS = {
  primary: '#EC4899',
  secondary: '#9333EA',
  black: '#000000',
  gray: '#737373',
  success: '#22C55E',
  error: '#EF4444'
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label,
  unit = '£M'
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-neutral-200 shadow-md rounded-md text-sm" data-id="element-1695">
        <p className="font-medium text-black mb-2" data-id="element-1696">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1 last:mb-0" data-id="element-1697">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color || entry.fill
        }} data-id="element-1698" />
            <span className="text-neutral-600" data-id="element-1699">{entry.name}:</span>
            <span className="font-medium text-black" data-id="element-1700">
              {unit === '£M' ? '£' : ''}
              {Math.abs(entry.value).toLocaleString()}
              {unit === '%' ? '%' : unit === '£K' ? 'K' : 'M'}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function FinancialPerformance() {
  return <div className="space-y-8 animate-in" data-id="element-1701">
      {/* Header */}
      <div className="flex justify-between items-start" data-id="element-1702">
        <div data-id="element-1703">
          <h1 className="text-2xl font-bold text-black" data-id="element-1704">
            Financial Performance
          </h1>
          <p className="text-neutral-500 mt-1" data-id="element-1705">
            Profitability and growth tracking • FY 2024
          </p>
        </div>
        <button className="px-4 py-2 bg-pink-500 text-white rounded-md text-sm font-medium hover:bg-pink-600 transition-colors flex items-center gap-2" data-id="element-1706">
          <Download size={16} data-id="element-1707" />
          Export
        </button>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1708">
        {/* Premium Trend */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 h-96" data-id="element-1709">
          <h2 className="text-base font-medium text-black mb-6" data-id="element-1710">
            Premium Trend
          </h2>
          <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1711">
            <LineChart data={premiumTrendData} margin={{
            top: 5,
            right: 20,
            left: 0,
            bottom: 5
          }} data-id="element-1712">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" data-id="element-1713" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} dy={10} data-id="element-1714" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} tickFormatter={value => `£${value}M`} data-id="element-1715" />
              <Tooltip content={<CustomTooltip unit="£M" data-id="element-1717" />} data-id="element-1716" />
              <Legend verticalAlign="top" align="right" wrapperStyle={{
              paddingBottom: '20px',
              fontSize: '12px'
            }} iconType="circle" iconSize={8} data-id="element-1718" />
              <Line type="monotone" dataKey="gwp" name="GWP" stroke={COLORS.black} strokeWidth={2} dot={false} activeDot={{
              r: 4,
              fill: COLORS.black
            }} data-id="element-1719" />
              <Line type="monotone" dataKey="earned" name="Earned" stroke={COLORS.secondary} strokeWidth={2} dot={false} activeDot={{
              r: 4,
              fill: COLORS.secondary
            }} data-id="element-1720" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Premium Breakdown */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 h-96" data-id="element-1721">
          <h2 className="text-base font-medium text-black mb-6" data-id="element-1722">
            Premium Breakdown
          </h2>
          <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1723">
            <BarChart data={waterfallData} margin={{
            top: 5,
            right: 20,
            left: 0,
            bottom: 5
          }} data-id="element-1724">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" data-id="element-1725" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} dy={10} data-id="element-1726" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} tickFormatter={value => `£${Math.abs(value)}M`} data-id="element-1727" />
              <Tooltip content={<CustomTooltip unit="£M" data-id="element-1729" />} data-id="element-1728" />
              <ReferenceLine y={0} stroke="#A3A3A3" data-id="element-1730" />
              <Bar dataKey="value" name="Amount" radius={[4, 4, 0, 0]} data-id="element-1731">
                {waterfallData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-1732" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Underwriting Result */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 h-96" data-id="element-1733">
          <h2 className="text-base font-medium text-black mb-6" data-id="element-1734">
            Underwriting Result
          </h2>
          <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1735">
            <BarChart data={underwritingData} margin={{
            top: 5,
            right: 20,
            left: 0,
            bottom: 5
          }} data-id="element-1736">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" data-id="element-1737" />
              <XAxis dataKey="quarter" axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} dy={10} data-id="element-1738" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} tickFormatter={value => `£${value}M`} data-id="element-1739" />
              <Tooltip content={<CustomTooltip unit="£M" data-id="element-1741" />} data-id="element-1740" />
              <Legend verticalAlign="top" align="right" wrapperStyle={{
              paddingBottom: '20px',
              fontSize: '12px'
            }} iconType="circle" iconSize={8} data-id="element-1742" />
              <Bar dataKey="premium" name="Premium" stackId="a" fill={COLORS.black} radius={[0, 0, 4, 4]} data-id="element-1743" />
              <Bar dataKey="claims" name="Claims" stackId="a" fill={COLORS.error} data-id="element-1744" />
              <Bar dataKey="expenses" name="Expenses" stackId="a" fill={COLORS.gray} radius={[4, 4, 0, 0]} data-id="element-1745" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Investment Performance */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 h-96" data-id="element-1746">
          <h2 className="text-base font-medium text-black mb-6" data-id="element-1747">
            Investment Performance
          </h2>
          <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1748">
            <ComposedChart data={investmentData} margin={{
            top: 5,
            right: 20,
            left: 0,
            bottom: 5
          }} data-id="element-1749">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" data-id="element-1750" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} dy={10} data-id="element-1751" />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} tickFormatter={value => `£${value}K`} data-id="element-1752" />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
              fill: '#737373',
              fontSize: 12
            }} tickFormatter={value => `${value}%`} data-id="element-1753" />
              <Tooltip content={<CustomTooltip unit="£K" data-id="element-1755" />} data-id="element-1754" />
              <Legend verticalAlign="top" align="right" wrapperStyle={{
              paddingBottom: '20px',
              fontSize: '12px'
            }} iconType="circle" iconSize={8} data-id="element-1756" />
              <Bar yAxisId="left" dataKey="income" name="Income" fill={COLORS.success} radius={[4, 4, 0, 0]} barSize={16} data-id="element-1757" />
              <Line yAxisId="right" type="monotone" dataKey="returns" name="Returns %" stroke={COLORS.secondary} strokeWidth={2} dot={false} activeDot={{
              r: 4
            }} data-id="element-1758" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}